import React, { useState } from "react";
import { Card, Button, Table, Space, Tag, Badge, Modal, message, Dropdown } from "antd";
import { useLoadList, useDelete } from "../../../hooks";
import { useTableSelection } from "../../../hooks";
import { flightLegService } from "../../../api/enhancedApi";
import { formatDateTime, formatMinutesToHHMM } from "../../../utils/dateUtils";
import FlightLegFilter from "./components/FlightLegFilter";
import FlightLegForm from "./components/FlightLegForm";
import { PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DownOutlined } from "@ant-design/icons";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { DEFAULT_STC_TYPE, DEFAULT_LEG_TYPE } from "../../../constants/flightConstants";

const { confirm } = Modal;

// 删除本地格式化函数，使用共用的工具函数
// const formatMinutesToHHMM = (minutes) => {
//     if (!minutes && minutes !== 0) return "-";
//     const hours = Math.floor(minutes / 60);
//     const mins = minutes % 60;
//     return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
// };

const FlightLegList = () => {
    // 获取权限相关功能
    const { hasPermission } = useAuth();

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
    });
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [formVisible, setFormVisible] = useState(false);
    const [currentLeg, setCurrentLeg] = useState(null);
    const [formMode, setFormMode] = useState("add"); // 'add' 或 'edit'

    // 构造API请求参数
    const apiParams = React.useMemo(() => {
        const params = {
            page: pagination.current,
            page_size: pagination.pageSize,
            ...filterParams,
        };

        return params;
    }, [pagination.current, pagination.pageSize, filterParams]);

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("flight-legs", flightLegService.getFlightLegList, apiParams);

    // 使用自定义Hook处理表格选择，注意这里使用复合主键
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection } = useTableSelection({
        getRowKey: (record) => `${record.departure_airport}-${record.arrival_airport}`,
        dataSource: data?.items || [],
        pagination,
    });

    // 删除航段的Hook
    const deleteMutation = useDelete("flightLegs", flightLegService.deleteFlightLeg, {
        onSuccess: () => {
            message.success("删除成功");
            refetch();
        },
    });

    // 定义表格列
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "出发机场",
            dataIndex: "departure_airport",
            key: "departure_airport",
            width: 80,
            render: (text) => (
                <span className={`location-cell ${selectedLocation === text ? "location-highlight" : ""}`} onMouseEnter={() => setSelectedLocation(text)}>
                    {text}
                </span>
            ),
        },
        {
            title: "到达机场",
            dataIndex: "arrival_airport",
            key: "arrival_airport",
            width: 80,
            render: (text) => (
                <span className={`location-cell ${selectedLocation === text ? "location-highlight" : ""}`} onMouseEnter={() => setSelectedLocation(text)}>
                    {text}
                </span>
            ),
        },
        {
            title: "航段编号",
            dataIndex: "leg_number",
            key: "leg_number",
            width: 100,
        },
        {
            title: "航班性质",
            dataIndex: "stc_type",
            key: "stc_type",
            width: 100,
            render: (text) => text || "-", // 如果没有值则显示 '-'
        },
        // 【航段航路BH优化】隐藏轮挡时间列，BH将从航路自动获取
        // {
        //     title: "轮挡时间",
        //     dataIndex: "block_hour_minutes",
        //     key: "block_hour_minutes",
        //     width: 120,
        //     render: (text) => formatMinutesToHHMM(text),
        // },
        {
            title: "航段类型",
            dataIndex: "leg_type",
            key: "leg_type",
            width: 100,
            render: (text) => {
                let color = "default";
                if (text === "DOMESTIC") color = "blue";
                if (text === "INTERNATIONAL") color = "green";
                if (text === "REGIONAL") color = "orange";

                return <Tag color={color}>{text}</Tag>;
            },
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (is_active) => <Badge className="status-badge" status={is_active ? "success" : "error"} text={is_active ? "启用" : "停用"} />,
        },
        {
            title: "备注",
            dataIndex: "remarks",
            key: "remarks",
            width: 160,
            ellipsis: true,
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 120,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 150,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="masterdata:update">
                        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="masterdata:delete">
                        <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} disabled={record.is_active}>
                            删除
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 处理表格分页变化
    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
    };

    // 处理搜索
    const handleSearch = (values) => {
        // 处理搜索参数，确保与后端API参数匹配
        const params = {};

        if (values.departure_airport) {
            params.departure_airport = values.departure_airport;
        }

        if (values.arrival_airport) {
            params.arrival_airport = values.arrival_airport;
        }

        if (values.leg_number) {
            params.leg_number = values.leg_number;
        }

        // 处理状态参数，确保正确传递布尔值
        if (values.is_active !== undefined && values.is_active !== null) {
            params.is_active = values.is_active;
        }

        setFilterParams(params);
        setPagination({
            ...pagination,
            current: 1, // 重置到第一页
        });
    };

    // 重置搜索
    const handleReset = () => {
        setFilterParams({});
        setPagination({
            ...pagination,
            current: 1,
        });
    };

    // 添加航段
    const handleAdd = () => {
        setFormMode("add");
        setCurrentLeg({
            departure_airport: "",
            arrival_airport: "",
            leg_number: "",
            block_time_minutes: "",
            leg_type: DEFAULT_LEG_TYPE,
            stc_type: DEFAULT_STC_TYPE, // 添加默认值
            is_active: true,
            remarks: "",
        });
        setFormVisible(true);
    };

    // 编辑航段
    const handleEdit = (record) => {
        // 检查编辑权限
        if (!hasPermission("masterdata:update")) {
            message.error("您没有编辑航段的权限");
            return;
        }

        setFormMode("edit");
        setCurrentLeg(record);
        setFormVisible(true);
    };

    // 删除航段
    const handleDelete = (record) => {
        // 检查删除权限
        if (!hasPermission("masterdata:delete")) {
            message.error("您没有删除航段的权限");
            return;
        }

        if (record.is_active) {
            message.warning("启用状态的航段不能删除，请先停用");
            return;
        }

        confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除 ${record.departure_airport}-${record.arrival_airport} 航段吗？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                // 删除操作需要传递出发和到达机场作为复合主键
                deleteMutation.mutate({
                    departure_airport: record.departure_airport,
                    arrival_airport: record.arrival_airport,
                });
            },
        });
    };

    // 批量删除航段
    const handleBatchDelete = () => {
        // 检查删除权限
        if (!hasPermission("masterdata:delete")) {
            message.error("您没有删除航段的权限");
            return;
        }

        const activeLegs = selectedRows.filter((item) => item.is_active);
        if (activeLegs.length > 0) {
            message.warning("选择的航段中包含启用状态的航段，请先停用后再删除");
            return;
        }

        confirm({
            title: "确认批量删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedRowKeys.length} 个航段吗？此操作不可恢复。`,
            okText: "确认",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                // 串行执行删除操作
                Promise.all(
                    selectedRowKeys.map((key) => {
                        const [departure, arrival] = key.split("-");
                        return flightLegService.deleteFlightLeg({
                            departure_airport: departure,
                            arrival_airport: arrival,
                        });
                    }),
                )
                    .then(() => {
                        message.success(`成功删除 ${selectedRowKeys.length} 个航段`);
                        clearSelection();
                        refetch();
                    })
                    .catch((error) => {
                        message.error(`批量删除失败: ${error.message}`);
                    });
            },
        });
    };

    // 批量启用航段
    const handleBatchEnable = () => {
        // 检查更新权限
        if (!hasPermission("masterdata:update")) {
            message.error("您没有更新航段的权限");
            return;
        }

        // 这里假设有批量启用的API，实际情况可能需要调整
        message.info("批量启用功能正在开发中");
    };

    // 批量停用航段
    const handleBatchDisable = () => {
        // 检查更新权限
        if (!hasPermission("masterdata:update")) {
            message.error("您没有更新航段的权限");
            return;
        }

        // 这里假设有批量停用的API，实际情况可能需要调整
        message.info("批量停用功能正在开发中");
    };

    // 批量操作菜单项
    const batchActionItems = [
        {
            key: "delete",
            label: "批量删除",
            disabled: selectedRowKeys.length === 0 || !hasPermission("masterdata:delete"),
            onClick: handleBatchDelete,
        },
        {
            key: "enable",
            label: "批量启用",
            disabled: selectedRowKeys.length === 0 || !hasPermission("masterdata:update"),
            onClick: handleBatchEnable,
        },
        {
            key: "disable",
            label: "批量停用",
            disabled: selectedRowKeys.length === 0 || !hasPermission("masterdata:update"),
            onClick: handleBatchDisable,
        },
    ];

    // 表单提交成功
    const handleFormSuccess = () => {
        setFormVisible(false);
        refetch();
    };

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">航段管理</div>
                <div className="page-action-compact">
                    <PermissionGuard permission="masterdata:create">
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            添加航段
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="masterdata:create">
                        <Button icon={<UploadOutlined />}>批量导入</Button>
                    </PermissionGuard>
                    <PermissionGuard permission={["masterdata:update", "masterdata:delete"]} requireAll={false}>
                        <Dropdown menu={{ items: batchActionItems }} disabled={selectedRowKeys.length === 0}>
                            <Button>
                                批量操作 <DownOutlined />
                            </Button>
                        </Dropdown>
                    </PermissionGuard>
                </div>
            </div>

            {/* 搜索过滤 */}
            <Card className="filter-card">
                <FlightLegFilter onSearch={handleSearch} onReset={handleReset} />
            </Card>

            {/* 选中项统计 */}
            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info">
                    已选择 <span className="selected-count">{selectedRowKeys.length}</span> 项
                    <Button type="link" onClick={clearSelection}>
                        清空
                    </Button>
                </div>
            )}

            {/* 航段列表 */}
            <Card className="data-card">
                <Table
                    ref={tableRef}
                    size="small"
                    rowSelection={hasPermission("masterdata:delete") || hasPermission("masterdata:update") ? rowSelection : null}
                    columns={columns}
                    dataSource={data?.items || []}
                    rowKey={(record) => `${record.departure_airport}-${record.arrival_airport}`}
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1500 }}
                    onRow={getRowProps}
                />
            </Card>

            {/* 添加/编辑航段表单 */}
            {formVisible && <FlightLegForm visible={formVisible} mode={formMode} initialValues={currentLeg} onCancel={() => setFormVisible(false)} onSuccess={handleFormSuccess} />}
        </div>
    );
};

export default FlightLegList;
